import { Fragment, useId, type ReactNode } from "react";
import { Listbox, Transition } from "@headlessui/react";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { cn } from "./utils/cn";
import Label from "./Label";
import FloatingLabel from "./FloatingLabel";
import FieldError from "./FieldError";

export interface SelectOption<T extends string = string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  /** `default` — label above; `compact` — label on the top border (Historic Worth look). */
  variant?: "default" | "compact";
  className?: string;
  ["data-cy"]?: string;
}

/**
 * Canonical dropdown select built on Headless UI Listbox, styled to match
 * TextField. Supersedes ad-hoc native <select> usages.
 */
function Select<T extends string = string>({
  value,
  onChange,
  options,
  label,
  placeholder,
  required,
  disabled,
  error,
  variant = "default",
  className,
  ...props
}: SelectProps<T>) {
  const reactId = useId();
  const errorId = error ? `${reactId}-error` : undefined;
  const selected = options.find((o) => o.value === value);
  const compact = variant === "compact";

  return (
    <div className={className}>
      {!compact && label && (
        <Label required={required} readOnly={disabled}>
          {label}
        </Label>
      )}
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="group relative">
          {compact && label && (
            <FloatingLabel required={required} error={Boolean(error)}>
              {label}
            </FloatingLabel>
          )}
          <Listbox.Button
            data-cy={props["data-cy"]}
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            aria-label={
              compact && typeof label === "string" ? label : undefined
            }
            className={cn(
              "transition-colors flex h-control w-full items-center justify-between gap-3 rounded-control border bg-surface px-3 text-start text-base font-normal leading-none",
              error ? "border-danger" : "border-border-subtle",
              "focus-visible:outline-none data-[headlessui-state=open]:border-secondary",
              compact
                ? "focus-visible:border-secondary"
                : "focus-visible:ring-2 focus-visible:ring-secondary/15 focus-visible:ring-offset-0",
              disabled && "cursor-not-allowed opacity-60"
            )}
          >
            <span
              className={cn(
                "truncate",
                selected ? "text-menu" : "text-menu/40"
              )}
            >
              {selected ? selected.label : placeholder ?? ""}
            </span>
            <KeyboardArrowDownIcon
              className="shrink-0 text-menu/60"
              sx={{ fontSize: 20 }}
              aria-hidden
            />
          </Listbox.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out-soft duration-150"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Listbox.Options className="absolute z-dropdown mt-1 max-h-60 w-full overflow-auto rounded-xl border border-border-subtle bg-surface p-1.5 text-base font-normal shadow-e3 focus:outline-none">
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={({ active }) =>
                    cn(
                      "flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-start text-menu",
                      active && "bg-surface-subtle",
                      option.disabled && "cursor-not-allowed opacity-50"
                    )
                  }
                >
                  {({ selected: isSelected }) => (
                    <>
                      <span
                        className={cn(
                          "truncate",
                          isSelected && "font-semibold text-secondary"
                        )}
                      >
                        {option.label}
                      </span>
                      {isSelected && (
                        <span className="text-secondary">
                          <CheckIcon sx={{ fontSize: 18 }} aria-hidden />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
}

export default Select;
